import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkles, ScanLine, Target, FileText, Users, BarChart3,
  ArrowRight, Check, Zap, Globe, MapPin,
  UtensilsCrossed, Stethoscope, Scissors, Hotel, Tent, Wrench, GraduationCap, Home,
  HelpCircle, AlertTriangle as AlertTriangleIcon, TrendingUp, Loader2, ShieldCheck,
} from "lucide-react";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";
import YusiVisibilityCycleSection from "@/components/YusiVisibilityCycleSection";
import { useLang } from "@/contexts/LanguageContext";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/ef138ce5-22e9-4874-b9b7-9f141a488713/images/28063146d8ef0870d93d48742b12d010db94c00050f47599c4bdb432f47fd0e3.png";
const NET_BG = "https://static.prod-images.emergentagent.com/jobs/ef138ce5-22e9-4874-b9b7-9f141a488713/images/da448473ef085517f91972398eadbcffb5bd3002523fd69ebfc52bb5cb560253.png";

// ---------------------------------------------------------------------------
// Bilingual copy dictionary
// ---------------------------------------------------------------------------
// Single source of truth for landing page text in EN (default `/`) and TR (`/tr`).
// We deliberately keep this as plain JS objects (no i18n lib) — easier to review,
// no extra dependencies, and lets us tune phrasing per language without
// awkward interpolation.
const COPY = {
  en: {
    htmlLang: "en",
    pageTitle: "uSeen — Get found when customers ask AI about local businesses",
    metaDescription:
      "uSeen helps local businesses show up when people ask AI tools like ChatGPT, Gemini, and Google AI for recommendations. Check your AI visibility for free.",
    nav: {
      howItWorks: "How it works",
      product: "Product",
      pricing: "Pricing",
      sample: "Sample report",
      login: "Log in",
      register: "Start free",
    },
    hero: {
      badge: "Be visible in AI answers",
      title1: "Make Your Business",
      title2: "More Visible to AI",
      sub: (
        <>
          uSeen analyzes your business, turns visibility gaps into{" "}
          <span className="text-white">Yusi Actions</span>, and prepares the
          content AI systems need to understand and recommend you on{" "}
          <span className="text-white">ChatGPT</span>,{" "}
          <span className="text-white">Gemini</span> and{" "}
          <span className="text-white">Google AI</span>. You review,
          approve and use the outputs.
        </>
      ),
      bubble: "Let's grow your visibility together.",
      ctaPrimary: "Run my free visibility check",
      ctaSecondary: "See a sample report",
      helper: "Your first check is ready in a few minutes.",
    },
    promise: {
      eyebrow: "Ready for the AI era",
      headline: "uSeen prepares — you review and approve.",
      body: (
        <>
          uSeen analyzes your digital presence so AI tools can{" "}
          <span className="text-white font-medium">understand</span> and{" "}
          <span className="text-white font-medium">recommend</span> your
          business. Yusi finds the{" "}
          <span className="text-white font-medium">missing signals</span> and
          prepares <span className="text-white font-medium">ready-to-use outputs</span>{" "}
          you can approve and use — with your permission.
        </>
      ),
    },
    steps: {
      eyebrow: "How it works",
      title: "AI visibility in 6 steps",
      items: [
        { icon: Target, title: "Add your business", desc: "Share your sector, location, and services." },
        { icon: BarChart3, title: "See your AI visibility score", desc: "Evaluated across 7 dimensions." },
        { icon: FileText, title: "Review Yusi Actions", desc: "A prioritized, doable list." },
        { icon: Sparkles, title: "See outputs prepared by Yusi", desc: "Ready-to-use content for each action." },
        { icon: Check, title: "Approve, edit or reject", desc: "You stay in control of every output." },
        { icon: ScanLine, title: "Mark what you used and rescan", desc: "Track progress, refresh your score." },
      ],
    },
    features: {
      eyebrow: "Product",
      title: "What uSeen does",
      items: [
        { icon: Globe, title: "AI Visibility Analysis", desc: "We measure how AI tools see your business." },
        { icon: Target, title: "Yusi Actions", desc: "Prioritized, doable actions from real gaps." },
        { icon: Sparkles, title: "Ready-to-use Content", desc: "Outputs prepared for each action you can use right away." },
        { icon: Check, title: "Approval Workflow", desc: "Approve, edit, reject or mark outputs as used." },
        { icon: HelpCircle, title: "AI Question Tests", desc: "Check how you answer real questions in your sector." },
        { icon: Users, title: "Competitor Comparison", desc: "Side-by-side analysis of up to 3 competitors." },
        { icon: ScanLine, title: "Rescan and Reporting", desc: "Track changes and your visibility score over time." },
        { icon: ShieldCheck, title: "Yusi Permissions", desc: "Understand what Yusi can do today and what will need your permission later." },
      ],
    },
    sectors: {
      eyebrow: "Who it&apos;s for",
      title: "Every local business that serves a neighborhood",
      items: [
        { icon: UtensilsCrossed, name: "Restaurants" },
        { icon: Stethoscope, name: "Dental clinics" },
        { icon: Scissors, name: "Salons & beauty" },
        { icon: Hotel, name: "Hotels" },
        { icon: Tent, name: "Campgrounds" },
        { icon: Wrench, name: "Auto services" },
        { icon: GraduationCap, name: "Private schools" },
        { icon: Home, name: "Real estate offices" },
      ],
    },
    ethical: {
      eyebrow: "Ready for the AI era",
      title: "Get ready to stand out in AI answers with Yusi",
      body: (
        <>
          uSeen helps your business{" "}
          <span className="text-white font-medium">become easier for AI tools to understand</span>{" "}
          and <span className="text-white font-medium">more likely to be recommended</span>.
          Yusi <span className="text-white font-medium">measures your AI visibility</span>,{" "}
          <span className="text-white font-medium">prepares ready-to-use outputs</span>, and{" "}
          <span className="text-white font-medium">brings them to you for review</span>.
        </>
      ),
      body2: (
        <>
          You stay in control before anything is applied to your website, FAQs,
          service descriptions or Google Business Profile —{" "}
          <span className="text-slate-200 font-medium">always with your permission</span>.
        </>
      ),
      bullets: [
        "Measure your AI visibility",
        "Find missing content and trust signals",
        "Review outputs prepared by Yusi",
        "Use approved content",
        "Track improvements with rescans",
        "Prepare for future permission-based automation",
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      title: "Clear, simple, flexible",
      allPlans: "See all plans →",
      plans: [
        {
          id: "starter",
          name: "Starter",
          price: "₺790",
          period: "/ month",
          desc: "For single businesses starting their AI visibility journey.",
          accent: "default",
          cta: "Choose Starter",
          features: [
            "1 business",
            "AI visibility score",
            "Yusi Actions",
            "10 prepared outputs per month",
            "2 rescans per month",
            "Basic report",
            "Yusi Permissions panel",
          ],
        },
        {
          id: "growth",
          name: "Growth",
          price: "₺1,990",
          period: "/ month",
          desc: "For businesses that want to improve AI visibility consistently.",
          featured: true,
          badge: "Most Popular",
          accent: "default",
          cta: "Choose Growth",
          features: [
            "Everything in Starter",
            "40 prepared outputs per month",
            "Weekly rescan",
            "AI question tests",
            "Competitor comparison",
            "Approve / reject / used workflow",
            "PDF report",
            "Progress tracking",
          ],
        },
        {
          id: "pro",
          name: "Pro",
          price: "₺4,990",
          period: "/ month",
          desc: "For multi-location businesses, agencies and advanced visibility management.",
          accent: "default",
          cta: "Choose Pro",
          features: [
            "3 businesses or locations",
            "Everything in Growth",
            "150 prepared outputs per month",
            "More frequent rescans",
            "Advanced PDF reports",
            "Priority support",
            "Future integration readiness",
          ],
        },
      ],
    },
    cta: {
      title: "Is your business ready for the AI era?",
      sub: "Start a free 5-minute check and see your AI visibility score.",
      btn: "Get my free score",
    },
    faq: {
      eyebrow: "FAQ",
      title: "Frequently Asked Questions",
      sub: "Quick answers to the questions we hear the most.",
      items: [
        { q: "What is uSeen AI?", a: "uSeen AI is an AI Visibility platform that analyzes your business’s digital presence and helps make it easier for AI systems to understand, find, and recommend your business." },
        { q: "Is uSeen an SEO tool?", a: "No. uSeen is not a traditional SEO tool. SEO focuses mainly on search engines, while uSeen focuses on how AI systems such as ChatGPT, Gemini, and similar tools may understand and recommend your business." },
        { q: "What does the AI Visibility Score mean?", a: "The AI Visibility Score shows how clear, trustworthy, location-ready, and recommendation-ready your business information is for AI systems." },
        { q: "Is this score a live ChatGPT or Gemini ranking?", a: "No. The score is not a live ranking result. It is a readiness score based on digital signals that may help AI systems better understand your business." },
        { q: "What are Yusi Actions?", a: "Yusi Actions turn analysis findings into clear, actionable tasks. Yusi does not only tell you what to fix; it can also prepare ready-to-use content for you." },
        { q: "Does uSeen automatically publish content?", a: "Not at this stage. uSeen prepares the content, and you review, approve, and apply it on your own website or relevant platforms." },
        { q: "Who is uSeen for?", a: "uSeen is designed for local businesses, service providers, restaurants, beauty centers, clinics, auto services, education providers, and small to medium-sized businesses that want to become more visible in AI-driven discovery." },
        { q: "What does the free visibility check include?", a: "The free check helps you understand your current AI visibility readiness and see the main areas that can be improved." },
      ],
    },
    toast: {
      success: "Got it. The uSeen team will reach out shortly.",
      error: "Could not send your request.",
      done: "Request received ✓",
    },
  },

  tr: {
    htmlLang: "tr",
    pageTitle: "uSeen — Yapay zekâ tavsiyelerinde görünür olun",
    metaDescription:
      "uSeen, işletmenizin ChatGPT, Gemini ve Google AI gibi yapay zekâlarda nasıl göründüğünü analiz eder. Ücretsiz görünürlük kontrolü yapın.",
    nav: {
      howItWorks: "Nasıl Çalışır",
      product: "Kapsam",
      pricing: "Fiyatlandırma",
      sample: "Örnek Rapor",
      login: "Giriş Yap",
      register: "Ücretsiz Başla",
    },
    hero: {
      badge: "Yapay zekâlarda görünür ol",
      title1: "İşletmeni Yapay Zekâlarda",
      title2: "Daha Görünür Hale Getir",
      sub: (
        <>
          uSeen işletmenizi analiz eder, eksikleri{" "}
          <span className="text-white">Yusi Aksiyonlarına</span> çevirir ve
          yapay zekâların sizi daha iyi anlaması için gerekli içerikleri sizin
          için hazırlar —{" "}
          <span className="text-white">ChatGPT</span>,{" "}
          <span className="text-white">Gemini</span> ve{" "}
          <span className="text-white">Google AI</span> üzerinde. Siz inceler,
          onaylar ve kullanırsınız.
        </>
      ),
      bubble: "Görünürlüğünü birlikte artırıyoruz.",
      ctaPrimary: "Ücretsiz Görünürlük Kontrolü Yap",
      ctaSecondary: "Örnek Raporu Gör",
      helper: "İlk analiz birkaç dakika içinde hazır olur.",
    },
    promise: {
      eyebrow: "AI Çağı İçin Hazırlık",
      headline: "uSeen hazırlar — siz inceler ve onaylarsınız.",
      body: (
        <>
          uSeen, işletmenizin yapay zekalar tarafından daha iyi{" "}
          <span className="text-white font-medium">anlaşılması</span> ve{" "}
          <span className="text-white font-medium">önerilebilir</span> hale gelmesi
          için dijital varlığınızı{" "}
          <span className="text-white font-medium">analiz eder</span>. Yusi eksik
          sinyalleri{" "}
          <span className="text-white font-medium">bulur</span> ve kullanıma hazır{" "}
          <span className="text-white font-medium">çıktılar hazırlar</span> —
          siz onaylar ve kullanırsınız.
        </>
      ),
    },
    steps: {
      eyebrow: "Nasıl Çalışır",
      title: "6 adımda AI görünürlüğü",
      items: [
        { icon: Target, title: "İşletmeni ekle", desc: "Sektör, lokasyon ve hizmetlerini paylaş." },
        { icon: BarChart3, title: "AI görünürlük skorunu gör", desc: "7 farklı boyutta değerlendirme." },
        { icon: FileText, title: "Yusi Aksiyonlarını incele", desc: "Önceliklendirilmiş, uygulanabilir liste." },
        { icon: Sparkles, title: "Yusi'nin hazırladığı çıktıları gör", desc: "Her aksiyon için kullanıma hazır içerik." },
        { icon: Check, title: "Onayla, düzenle veya reddet", desc: "Kontrol her zaman sizde." },
        { icon: ScanLine, title: "Kullandıklarını işaretle ve yeniden tara", desc: "Gelişimi takip et, skorunu güncelle." },
      ],
    },
    features: {
      eyebrow: "Ürün",
      title: "uSeen ne yapar?",
      items: [
        { icon: Globe, title: "AI Görünürlük Analizi", desc: "AI sistemlerinin işletmenizi nasıl gördüğünü ölçeriz." },
        { icon: Target, title: "Yusi Aksiyonları", desc: "Gerçek eksiklerden çıkarılmış, uygulanabilir aksiyonlar." },
        { icon: Sparkles, title: "Hazır İçerik Üretimi", desc: "Her aksiyon için kullanıma hazır çıktı." },
        { icon: Check, title: "Onay Akışı", desc: "Onaylayın, düzenleyin, reddedin veya kullanıldı olarak işaretleyin." },
        { icon: HelpCircle, title: "AI Soru Testleri", desc: "Sektörünüze özgü sorulara hazırlığınızı görün." },
        { icon: Users, title: "Rakip Karşılaştırması", desc: "3 rakibe kadar yan yana analiz." },
        { icon: ScanLine, title: "Yeniden Tarama ve Raporlama", desc: "Değişiklikleri ve skoru zaman içinde takip edin." },
        { icon: ShieldCheck, title: "Yusi İzinleri", desc: "Yusi'nin bugün neleri yapabildiğini ve gelecekte hangi izinlerin gerekeceğini görün." },
      ],
    },
    sectors: {
      eyebrow: "Kimler İçin",
      title: "Lokal hizmet veren tüm işletmeler",
      items: [
        { icon: UtensilsCrossed, name: "Restoranlar" },
        { icon: Stethoscope, name: "Diş Klinikleri" },
        { icon: Scissors, name: "Güzellik Merkezleri" },
        { icon: Hotel, name: "Oteller" },
        { icon: Tent, name: "Kamp Alanları" },
        { icon: Wrench, name: "Oto Servisleri" },
        { icon: GraduationCap, name: "Özel Okullar" },
        { icon: Home, name: "Emlak Ofisleri" },
      ],
    },
    ethical: {
      eyebrow: "AI Çağı İçin Hazırlık",
      title: "AI aramalarında öne çıkmaya Yusi ile hazırlanın",
      body: (
        <>
          uSeen, işletmenizin{" "}
          <span className="text-white font-medium">yapay zekalar tarafından daha iyi anlaşılması</span> ve{" "}
          <span className="text-white font-medium">önerilebilir hale gelmesi</span> için dijital varlığınızı{" "}
          <span className="text-white font-medium">analiz eder</span>. Yusi{" "}
          <span className="text-white font-medium">eksik sinyalleri bulur</span> ve{" "}
          <span className="text-white font-medium">kullanıma hazır çıktıları sizin için hazırlar</span>.
        </>
      ),
      body2: (
        <>
          Web siteniz, SSS&apos;leriniz, hizmet açıklamalarınız veya Google İşletme
          metinlerinizde herhangi bir değişiklik yapılmadan önce karar her zaman{" "}
          <span className="text-slate-200 font-medium">sizin iznize ve onayınıza bağlıdır</span>.
        </>
      ),
      bullets: [
        "AI görünürlük seviyenizi ölçün",
        "Eksik içerik ve güven sinyallerini görün",
        "Yusi'nin hazırladığı çıktıları inceleyin",
        "Onayladığınız içerikleri kullanıma alın",
        "Uyguladığınız değişiklikleri yeniden tarama ile takip edin",
        "Gelecekte izin verdiğiniz bağlantılarla daha fazla işi Yusi'ye bırakın",
      ],
    },
    pricing: {
      eyebrow: "Fiyatlandırma",
      title: "Net, basit, esnek",
      allPlans: "Tüm planları gör →",
      plans: [
        {
          id: "starter",
          name: "Başlangıç",
          price: "₺790",
          period: "/ay",
          desc: "AI görünürlüğüne başlamak isteyen tek işletmeler için.",
          accent: "default",
          cta: "Başlangıç Paketini Seç",
          features: [
            "1 işletme",
            "AI Görünürlük Skoru",
            "Yusi Aksiyonları",
            "Aylık 10 hazır çıktı",
            "Aylık 2 yeniden tarama",
            "Temel rapor",
            "Yusi İzinleri paneli",
          ],
        },
        {
          id: "growth",
          name: "Büyüme",
          price: "₺1.990",
          period: "/ay",
          desc: "AI görünürlüğünü düzenli artırmak isteyen işletmeler için.",
          featured: true,
          badge: "En Çok Tercih Edilen",
          accent: "default",
          cta: "Büyüme Paketini Seç",
          features: [
            "Starter'daki her şey",
            "Aylık 40 hazır çıktı",
            "Haftalık yeniden tarama",
            "AI soru testleri",
            "Rakip karşılaştırması",
            "Onayla / reddet / kullandım akışı",
            "PDF rapor",
            "Gelişim takibi",
          ],
        },
        {
          id: "pro",
          name: "Profesyonel",
          price: "₺4.990",
          period: "/ay",
          desc: "Çoklu lokasyon, ajans ve gelişmiş görünürlük yönetimi için.",
          accent: "default",
          cta: "Profesyonel Paketi Seç",
          features: [
            "3 işletme veya lokasyon",
            "Growth'taki her şey",
            "Aylık 150 hazır çıktı",
            "Daha sık yeniden tarama",
            "Gelişmiş PDF raporlar",
            "Öncelikli destek",
            "Gelecekteki entegrasyonlara hazırlık",
          ],
        },
      ],
    },
    cta: {
      title: "İşletmeniz AI çağına hazır mı?",
      sub: "5 dakikada ücretsiz analiz başlatın, AI Hazırlık ve Görünürlük Skorunuzu görün.",
      btn: "Ücretsiz Skorumu Ölç",
    },
    faq: {
      eyebrow: "SSS",
      title: "Sıkça Sorulan Sorular",
      sub: "En çok aldığımız sorulara kısa yanıtlar.",
      items: [
        { q: "uSeen AI nedir?", a: "uSeen AI, işletmenizin yapay zekâlar tarafından daha kolay anlaşılması, bulunması ve önerilebilir hale gelmesi için dijital varlığınızı analiz eden bir AI Visibility platformudur." },
        { q: "uSeen SEO aracı mı?", a: "Hayır. uSeen klasik bir SEO aracı değildir. SEO daha çok arama motorlarına odaklanırken, uSeen ChatGPT, Gemini ve benzeri yapay zekâların işletmenizi nasıl anlayabileceğine ve önerebileceğine odaklanır." },
        { q: "AI Visibility Score neyi gösterir?", a: "AI Visibility Score, işletmenizin dijital bilgilerinin yapay zekâlar için ne kadar anlaşılır, güvenilir, konum olarak net ve önerilebilir olduğunu gösteren hazırlık skorudur." },
        { q: "Bu skor canlı ChatGPT veya Gemini sıralaması mı?", a: "Hayır. Skor canlı bir sıralama sonucu değildir. İşletmenizin yapay zekâlar tarafından daha iyi anlaşılmasına yardımcı olacak dijital sinyallerin değerlendirmesidir." },
        { q: "Yusi Aksiyonları nedir?", a: "Yusi Aksiyonları, analiz sonrası çıkan eksikleri yapılabilir görevlere dönüştürür. Yusi yalnızca ne yapmanız gerektiğini söylemez; çoğu zaman kullanabileceğiniz hazır metinleri de oluşturur." },
        { q: "uSeen içerikleri otomatik yayınlar mı?", a: "Şu anda hayır. uSeen içerikleri hazırlar, siz kontrol eder, onaylar ve kendi web sitenizde veya ilgili platformlarda uygularsınız." },
        { q: "Hangi işletmeler için uygundur?", a: "Yerel işletmeler, hizmet sağlayıcılar, restoranlar, güzellik merkezleri, klinikler, oto hizmetleri, eğitim kurumları ve dijitalde daha görünür olmak isteyen küçük ve orta ölçekli işletmeler için uygundur." },
        { q: "Ücretsiz görünürlük kontrolü ne sağlar?", a: "Ücretsiz kontrol, işletmenizin yapay zekâ görünürlüğü açısından mevcut durumunu anlamanıza yardımcı olur ve temel eksikleri görmenizi sağlar." },
      ],
    },
    toast: {
      success: "Talebiniz alındı. uSeen ekibi sizinle iletişime geçecek.",
      error: "Talep gönderilemedi",
      done: "Talep Alındı ✓",
    },
  },
};

// ---------------------------------------------------------------------------
// Language switcher — used in both desktop nav and mobile header. Renders as
// a minimal "EN / TR" toggle, with the inactive language as a Link to the
// other route so it works without JS and is crawlable.
// ---------------------------------------------------------------------------
function LangSwitch({ current }) {
  const base = "text-xs font-semibold tracking-wide px-2 py-1 rounded-md transition-colors";
  const active = "bg-white/10 text-white";
  const inactive = "text-slate-400 hover:text-white";
  return (
    <div
      className="inline-flex items-center gap-1 border border-white/10 rounded-lg px-1 py-0.5"
      data-testid="landing-lang-switch"
    >
      <Link
        to="/"
        className={`${base} ${current === "en" ? active : inactive}`}
        data-testid="lang-switch-en"
        aria-current={current === "en" ? "page" : undefined}
      >
        EN
      </Link>
      <span className="text-slate-600 text-xs">|</span>
      <Link
        to="/tr"
        className={`${base} ${current === "tr" ? active : inactive}`}
        data-testid="lang-switch-tr"
        aria-current={current === "tr" ? "page" : undefined}
      >
        TR
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main landing component — receives `lang` prop ("en" by default) and looks
// up all visible text from the `COPY` dictionary above. Layout/styles are
// shared between languages so we never drift.
// ---------------------------------------------------------------------------
export default function Landing({ lang = "en" }) {
  const t = COPY[lang] || COPY.en;
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { syncFromUrl } = useLang();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [doneIds, setDoneIds] = useState([]);

  // Sync app-wide language with the landing route the visitor explicitly
  // chose. So clicking the EN/TR pill (which routes to / or /tr) also
  // updates localStorage; on login they continue in that language.
  useEffect(() => { syncFromUrl(lang); }, [lang, syncFromUrl]);

  const selectPlan = async (plan) => {
    if (!user) {
      navigate("/kayit");
      return;
    }
    setLoadingPlan(plan.id);
    try {
      await api.post("/purchase-requests", { plan: plan.name });
      toast.success(t.toast.success);
      setDoneIds((arr) => [...arr, plan.id]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.toast.error);
    } finally {
      setLoadingPlan(null);
    }
  };

  // Smooth-scroll to anchor when arriving with a #hash (works for cross-route
  // footer links like /#nasil-calisir that React Router doesn't auto-scroll).
  // Re-runs on every hash change so links clicked while ALREADY on the
  // landing page (e.g. footer "AI visibility score") also scroll correctly.
  useEffect(() => {
    const target = location.hash ? location.hash.slice(1) : null;
    if (!target) return;
    const tid = setTimeout(() => {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(tid);
  }, [location.hash, location.pathname]);

  // Update <html lang> + document title/meta description per language.
  useEffect(() => {
    document.documentElement.lang = t.htmlLang;
    document.title = t.pageTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", t.metaDescription);

    // hreflang alternates — tells search engines `/` is the English version
    // and `/tr` is the Turkish version, with English as x-default. We use the
    // canonical production domain (useen.ai) regardless of the current host so
    // preview deploys don't pollute Google's index. Tags are tagged with a
    // shared data-attr so we own the set and can rewrite cleanly without
    // touching anything that index.html may declare in the future.
    const ORIGIN = "https://useen.ai";
    const alternates = [
      { hreflang: "en", href: `${ORIGIN}/` },
      { hreflang: "tr", href: `${ORIGIN}/tr` },
      { hreflang: "x-default", href: `${ORIGIN}/` },
    ];
    document
      .querySelectorAll('link[rel="alternate"][data-useen-hreflang]')
      .forEach((el) => el.remove());
    alternates.forEach(({ hreflang, href }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hreflang);
      link.setAttribute("href", href);
      link.setAttribute("data-useen-hreflang", "1");
      document.head.appendChild(link);
    });
  }, [t.htmlLang, t.pageTitle, t.metaDescription]);

  return (
    <div className="min-h-screen relative overflow-x-clip">
      <header
        className="sticky top-0 z-30 px-3 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2 border-b border-white/5 bg-[#0a0a14]/80 backdrop-blur-xl"
        data-testid="landing-header"
      >
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="inline-flex items-center bg-transparent border-0 p-0 cursor-pointer shrink-0"
          aria-label={lang === "en" ? "Back to top" : "Sayfanın başına dön"}
          data-testid="landing-logo-button"
        >
          <Logo />
        </button>
        <nav className="hidden md:flex items-center gap-7 text-sm text-slate-300">
          <a href="#nasil-calisir" className="hover:text-white transition-colors">{t.nav.howItWorks}</a>
          <a href="#urun" className="hover:text-white transition-colors">{t.nav.product}</a>
          <Link to="/fiyatlandirma" className="hover:text-white transition-colors" data-testid="nav-pricing">{t.nav.pricing}</Link>
          <Link to="/ornek-rapor" className="hover:text-white transition-colors" data-testid="nav-sample">{t.nav.sample}</Link>
        </nav>
        <div className="flex items-center gap-1.5 sm:gap-3">
          <LangSwitch current={lang} />
          <Link
            to="/giris"
            className="text-xs sm:text-sm text-slate-300 hover:text-white px-1.5 sm:px-2 py-1 whitespace-nowrap"
            data-testid="nav-login"
          >
            {t.nav.login}
          </Link>
          <Link
            to="/kayit"
            className="neon-btn text-white text-xs sm:text-sm font-medium rounded-lg px-2.5 sm:px-4 py-1.5 sm:py-2 whitespace-nowrap"
            data-testid="nav-register"
          >
            {t.nav.register}
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 lg:px-12 pt-12 pb-24">
        <div
          className="absolute inset-0 -z-10 opacity-40"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 30%, black, transparent 75%)",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-grid opacity-30" />

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1.55fr_1fr] gap-10 lg:gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs text-slate-300 mb-7" data-testid="hero-badge">
                <Zap size={12} className="text-cyan-400" />
                {t.hero.badge}
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] xl:text-[3.5rem] font-bold tracking-tight leading-[1.08] text-white" data-testid="hero-title">
                {t.hero.title1}
                <br />
                <span className="gradient-text">{t.hero.title2}</span>
              </h1>

              {/* Yusi on mobile — under H1, above sub */}
              <div className="lg:hidden mt-8 flex justify-center" data-testid="hero-yusi-mobile">
                <HeroYusi bubble={t.hero.bubble} compact />
              </div>

              <p className="mt-7 sm:mt-8 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-testid="hero-sub">
                {t.hero.sub}
              </p>

              <div className="mt-9 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link to="/kayit" className="neon-btn text-white font-semibold rounded-xl px-7 py-4 text-base inline-flex items-center justify-center gap-2" data-testid="hero-cta-primary">
                  {t.hero.ctaPrimary} <ArrowRight size={18} />
                </Link>
                <Link to="/ornek-rapor" className="ghost-btn rounded-xl px-7 py-4 text-base text-white font-medium inline-flex items-center justify-center gap-2" data-testid="hero-cta-secondary">
                  {t.hero.ctaSecondary}
                </Link>
              </div>
              <p className="mt-4 text-xs text-slate-500 text-center lg:text-left" data-testid="hero-helper-text">
                {t.hero.helper}
              </p>
            </motion.div>

            {/* Yusi on desktop — right column */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="hidden lg:flex justify-center items-center"
              data-testid="hero-yusi-desktop"
            >
              <HeroYusi bubble={t.hero.bubble} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Prominent ethical promise */}
      <section className="relative z-10 px-6 lg:px-12 pt-6 pb-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden glass-strong p-8 sm:p-10 border-blue-500/25"
          >
            <div className="absolute -top-16 -left-16 w-72 h-72 bg-purple-500/15 blur-3xl rounded-full" />
            <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-cyan-500/15 blur-3xl rounded-full" />
            <div className="relative flex flex-col sm:flex-row items-start gap-6">
              <div className="w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-blue-600/30 border border-white/10 flex items-center justify-center glow-blue">
                <TrendingUp size={26} className="text-cyan-300" strokeWidth={1.8} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300 mb-2">{t.promise.eyebrow}</div>
                <p className="font-display text-xl sm:text-2xl text-white leading-tight mb-3">
                  <span className="gradient-text font-bold">{t.promise.headline}</span>
                </p>
                <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
                  {t.promise.body}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Yusi mascot driven cycle */}
      <YusiVisibilityCycleSection lang={lang} />

      {/* How it works */}
      <section id="nasil-calisir" className="relative z-10 px-6 lg:px-12 py-20 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-3">{t.steps.eyebrow}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">{t.steps.title}</h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {t.steps.items.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="glass rounded-2xl p-5 card-glow transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center mb-4">
                  <s.icon size={18} className="text-cyan-300" strokeWidth={1.6} />
                </div>
                <div className="text-xs text-slate-500 font-mono mb-1">0{i + 1}</div>
                <div className="font-display font-semibold text-white mb-1.5 text-sm">{s.title}</div>
                <p className="text-xs text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* uSeen ne yapar */}
      <section id="urun" className="relative z-10 px-6 lg:px-12 py-20 border-t border-white/5">
        <span id="ai-gorunurluk-skoru" className="block -mt-24 pt-24" aria-hidden="true" />
        <div className="absolute inset-0 -z-10 opacity-15"
          style={{ backgroundImage: `url(${NET_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-purple-400 mb-3">{t.features.eyebrow}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">{t.features.title}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.features.items.map((a) => (
              <div key={a.title} className="glass rounded-2xl p-6 card-glow">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/10 border border-white/10 flex items-center justify-center mb-4">
                  <a.icon size={20} className="text-blue-300" strokeWidth={1.6} />
                </div>
                <div className="font-display font-semibold text-white mb-1">{a.title}</div>
                <p className="text-sm text-slate-400 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kimler için */}
      <section id="kimler" className="relative z-10 px-6 lg:px-12 py-20 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-3">{t.sectors.eyebrow}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">{t.sectors.title}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {t.sectors.items.map((s) => (
              <div key={s.name} className="glass rounded-2xl p-5 card-glow flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/15 to-purple-500/10 border border-white/10 flex items-center justify-center mb-3">
                  <s.icon size={22} className="text-cyan-300" strokeWidth={1.5} />
                </div>
                <div className="font-display text-sm font-medium text-white">{s.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get ready for AI search */}
      <section className="relative z-10 px-6 lg:px-12 py-16 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="glass-strong rounded-3xl p-8 sm:p-10 border-blue-500/20 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-blue-500/10 blur-3xl rounded-full" />
            <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-purple-500/10 blur-3xl rounded-full" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center">
                  <TrendingUp size={20} className="text-cyan-300" strokeWidth={1.8} />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">{t.ethical.title}</h3>
              </div>
              <p className="text-slate-300 leading-relaxed text-base sm:text-lg">{t.ethical.body}</p>
              <p className="text-slate-400 leading-relaxed text-sm sm:text-base mt-4">{t.ethical.body2}</p>
              <ul className="mt-6 space-y-2.5 text-sm sm:text-base text-slate-300">
                {t.ethical.bullets.map((b) => (
                  <li key={b} className="flex gap-2">
                    <Check size={15} className="text-emerald-400 mt-1 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="relative z-10 px-6 lg:px-12 py-20 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-3">{t.pricing.eyebrow}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white">{t.pricing.title}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {t.pricing.plans.map((p) => {
              const isDone = doneIds.includes(p.id);
              const cardCls = p.featured
                ? "glass-strong border-2 border-blue-500/45 shadow-[0_0_50px_rgba(59,130,246,0.20)]"
                : "glass border border-white/10";
              const badgeCls = p.featured
                ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-[0_0_18px_rgba(168,85,247,0.5)]"
                : "";
              const btnCls = p.featured
                ? "neon-btn text-white"
                : "ghost-btn text-white hover:bg-white/10";
              return (
                <div key={p.id} className={`rounded-2xl p-7 flex flex-col relative ${cardCls}`}>
                  {p.badge && (
                    <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-wider px-3 py-1 rounded-full ${badgeCls}`}>
                      {p.badge}
                    </div>
                  )}
                  <div className="font-display text-xl font-semibold text-white mb-1">{p.name}</div>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="font-mono text-3xl text-white">{p.price}</span>
                    {p.period && <span className="text-xs text-slate-500">{p.period}</span>}
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed mb-5 min-h-[40px]">{p.desc}</p>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="text-sm text-slate-300 flex gap-2 leading-relaxed">
                        <Check size={14} className="text-cyan-400 mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => selectPlan(p)}
                    disabled={loadingPlan === p.id || isDone}
                    className={`rounded-xl py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70 ${btnCls}`}
                    data-testid={`landing-plan-${p.id}`}
                  >
                    {loadingPlan === p.id && <Loader2 size={14} className="animate-spin" />}
                    {isDone ? t.toast.done : p.cta}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-6">
            <Link to="/fiyatlandirma" className="text-sm text-cyan-400 hover:text-cyan-300">{t.pricing.allPlans}</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        className="relative z-10 px-6 lg:px-12 py-20 border-t border-white/5 scroll-mt-24"
        data-testid="landing-faq"
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[11px] uppercase tracking-[0.2em] text-cyan-400 font-semibold">{t.faq.eyebrow}</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mt-3 mb-3">{t.faq.title}</h2>
            <p className="text-slate-400">{t.faq.sub}</p>
          </div>
          <div className="space-y-3">
            {t.faq.items.map((item, i) => (
              <details
                key={i}
                className="group glass rounded-2xl border border-white/10 overflow-hidden open:border-cyan-500/30 transition-colors"
                data-testid={`faq-item-${i + 1}`}
              >
                <summary className="cursor-pointer list-none flex items-start gap-4 px-5 py-4 select-none">
                  <span className="text-white font-medium flex-1">{item.q}</span>
                  <span
                    className="shrink-0 mt-1 text-cyan-400 text-xl leading-none transition-transform group-open:rotate-45"
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <div className="px-5 pb-5 pt-1 text-sm text-slate-300 leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative z-10 px-6 lg:px-12 py-20 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center glass-strong rounded-3xl p-12">
          <h3 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">{t.cta.title}</h3>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">{t.cta.sub}</p>
          <Link to="/kayit" className="neon-btn text-white font-semibold rounded-xl px-8 py-4 inline-flex items-center gap-2" data-testid="bottom-cta">
            {t.cta.btn} <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <SiteFooter lang={lang} />
    </div>
  );
}


/**
 * Hero-side Yusi block with a small speech bubble.
 * Used on Landing hero in two layouts:
 *  - desktop: rendered in the right column of the hero grid (compact=false)
 *  - mobile: rendered under the H1 inside the text column (compact=true)
 */
function HeroYusi({ bubble, compact = false }) {
  const size = compact ? "w-40 sm:w-48" : "w-[280px] xl:w-[320px]";
  return (
    <div className={`relative ${compact ? "" : "pr-2"}`}>
      {/* Speech bubble */}
      <div
        className={`absolute z-10 ${
          compact ? "-top-3 -right-2 sm:-right-6" : "-top-4 -left-6 xl:-left-10"
        } rounded-2xl glass-strong border border-cyan-400/30 px-3.5 py-2 text-xs sm:text-sm text-white shadow-[0_8px_30px_-10px_rgba(34,211,238,0.5)] max-w-[180px] sm:max-w-[220px] leading-snug`}
        data-testid="hero-yusi-bubble"
      >
        {bubble}
        <span
          className={`absolute w-2.5 h-2.5 bg-[#0a0a14] border-r border-b border-cyan-400/30 rotate-45 ${
            compact
              ? "bottom-0 left-6 translate-y-1/2"
              : "bottom-0 right-6 translate-y-1/2"
          }`}
        />
      </div>

      {/* Glow halo behind Yusi */}
      <div
        className={`absolute rounded-full bg-cyan-500/15 blur-3xl pointer-events-none ${
          compact ? "inset-2" : "inset-4"
        }`}
        aria-hidden="true"
      />

      {/* Yusi mascot */}
      <img
        src="/yusi/yusi-04-helpful-cheerful.png"
        alt="Yusi"
        className={`relative ${size} h-auto select-none animate-float`}
        draggable="false"
        data-testid="hero-yusi-image"
      />
    </div>
  );
}
